import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import Stack from '@mui/material/Stack';

import type { PayingAccountField } from '@/domain/paySettings/schemas';

import TextFieldRhf from '@/components/mui/Form/TextField/TextFieldRhf';
import AutocompleteField from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm/AutocompleteField';
import SelectField from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm/SelectField';
import IbanField from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm/IbanField';
import { getDependentFieldNames } from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm/utils';

type Props = {
  fields: PayingAccountField[];
  isFieldDisabled: (field: PayingAccountField) => boolean;
};

const PayingAccountFields: React.FC<Props> = ({ fields, isFieldDisabled }) => {
  const { control, setValue } = useFormContext();

  // Synchronous with the commit — a watch-based effect races the child's re-scoping.
  const clearDependentsOf = useCallback(
    (parentName: string) =>
      getDependentFieldNames(fields, parentName).forEach((name) =>
        setValue(name, '', { shouldValidate: true, shouldDirty: true }),
      ),
    [fields, setValue],
  );

  // `pt`: otherwise the first field's label is clipped under the dialog title.
  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {fields.map((field) => {
        if (field.type === 'iban') return <IbanField key={field.name} field={field} fields={fields} />;
        if (field.hidden) return null;

        const disabled = isFieldDisabled(field);

        switch (field.type) {
          case 'text':
            return (
              <TextFieldRhf
                key={field.name}
                name={field.name}
                control={control}
                label={field.label}
                required
                fullWidth
                disabled={disabled}
                size="small"
              />
            );
          case 'select':
            return <SelectField key={field.name} field={field} disabled={disabled} />;
          case 'autocomplete':
            return (
              <AutocompleteField
                key={field.name}
                field={field}
                disabled={disabled}
                onValueCommitted={clearDependentsOf}
              />
            );
          default:
            return null;
        }
      })}
    </Stack>
  );
};

export default PayingAccountFields;
